import { router } from 'expo-router';
import { useState } from 'react';
import { type ScrollView, StyleSheet, View } from 'react-native';

import { useAuthContext, useCRMEventsContext } from '@fhs/auth/src/hooks';
import { Button, tokens } from '@fhs/ui';
import RateLimitedErrorModal from '@fhs-legacy/frontend/src/pages/cart/cart-rate-limited-modal';
import { logCartContinueOrder } from '@fhs-legacy/frontend/src/pages/cart/payment/utils';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';
import { CART_PAGE_CONTINUE_BUTTON } from '@fhs-legacy/frontend/src/utils/test-ids';

import CartErrorAlerts from '../cart-error-alerts';

import { useContinueButtonStates } from './use-continue-button-states';

interface IContinueButtonProps {
  scrollViewRef: React.MutableRefObject<ScrollView>;
  setIsBadContinueAttempt: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function ContinueButton({
  scrollViewRef,
  setIsBadContinueAttempt,
}: IContinueButtonProps) {
  const [isDisabledForErrors, setIsDisabledForErrors] = useState(false);
  const { isAuthenticated } = useAuthContext();
  const { logRBIEvent } = useCRMEventsContext();

  const { isDisabledForBadState, isLoading, shouldContinue } = useContinueButtonStates();
  const showSignUpToContinue = useFlag('enable-signup-cart-button-text')

  const shouldNavigateToSignin = shouldContinue && !isAuthenticated && showSignUpToContinue;
  const shouldNavigateToCheckout =
    (shouldContinue && isAuthenticated) ||
    (shouldContinue && !showSignUpToContinue && !isAuthenticated);

  const handleContinueInCart = () => {
    /*  The continue button should show signin if the flag is enabled and the user is not logged in.
     *  This will take them to signin and return to the cart after completed. When the flag is turned off,
     *  the user will see the continue button and navigate to checkout. The user must have a donation
     *  amount selected to continue in any case.
     */

    logCartContinueOrder(logRBIEvent);
    // Open upsell modal, do we want to make this conditional with a flag?
    // router.setParams({ action: 'upsell', id: 'continue' });
    if (shouldNavigateToSignin) {
      router.navigate(routes.signIn);
    } else if (shouldNavigateToCheckout) {
      router.navigate(routes.cartPayment);
    } else {
      // Donation is required but not selected - scroll to donation section
      setIsBadContinueAttempt(true);
      scrollViewRef.current?.scrollTo({ y: 0, animated: true });
    }
  };

  return (
    <View style={styles.container}>
      <CartErrorAlerts
        cartButtonLoading={isLoading}
        setIsDisabledForErrors={setIsDisabledForErrors}
      />
      <RateLimitedErrorModal />

      <Button
        testID={CART_PAGE_CONTINUE_BUTTON}
        size="xl"
        loading={isLoading}
        disabled={isDisabledForBadState || isDisabledForErrors}
        onPress={handleContinueInCart}
        accessibilityRole="button"
        accessibilityLabel={
          shouldNavigateToSignin ? 'Sign up to continue with your order' : 'Continue to checkout'
        }
        accessibilityHint={
          isDisabledForBadState || isDisabledForErrors
            ? 'Please check the items in your cart and try again.'
            : 'Continues to the checkout screen'
        }
      >
        <Button.Text style={isDisabledForBadState || isDisabledForErrors ? { color: 'black' } : {}}>
          {shouldNavigateToSignin ? 'Sign Up to Continue' : 'Continue'}
        </Button.Text>
      </Button>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderTopWidth: 1,
    borderTopColor: tokens.colors.$blackOpacity10,
    width: '100%',
    padding: 16,
  },
});
